import { HTMLAttributes, forwardRef, memo } from 'react'
import { cn } from '@/lib/utils'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'hover' | 'metric' | 'compact'
  padding?: 'sm' | 'md' | 'lg' | 'none' | 'xs'
}

const Card = memo(forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', padding = 'md', children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
            // Modern card styles - sophisticated & interactive
            'rounded-xl border bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm',
            'border-gray-200/50 dark:border-gray-700/50',
            'text-gray-900 dark:text-gray-50',
            'transition-all duration-300 group',
        
            // Modern variants - sophisticated differences
        {
              'shadow-md hover:shadow-lg': variant === 'default',
              'shadow-md hover:shadow-xl hover:border-primary-300 dark:hover:border-primary-600 hover:-translate-y-2 hover:scale-[1.02]': variant === 'hover',
              'shadow-lg min-h-[160px] flex flex-col justify-between bg-gradient-to-br from-white/90 to-gray-50/90 dark:from-gray-800/90 dark:to-gray-900/90': variant === 'metric',
              'shadow-sm hover:shadow-md': variant === 'compact',
        },
        
            // Enhanced padding - more generous spacing
        {
              'p-2': padding === 'xs',
              'p-5': padding === 'sm',
              'p-7': padding === 'md',
              'p-9': padding === 'lg',
          'p-0': padding === 'none',
        },
        
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
))

Card.displayName = 'Card'

const CardHeader = memo(forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { compact?: boolean }>(
  ({ className, children, compact, ...props }, ref) => (
    <div 
      ref={ref} 
      className={cn(
        compact ? 'flex flex-col space-y-1 pb-3' : 'flex flex-col space-y-3 pb-6',
        className
      )} 
      {...props}
    >
      {children}
    </div>
  )
))

CardHeader.displayName = 'CardHeader'

const CardTitle = memo(forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement> & { compact?: boolean }>(
  ({ className, children, compact, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        compact ? 'text-base font-semibold leading-tight' : 'text-xl font-semibold leading-tight',
        'text-gray-900 dark:text-gray-50',
        'text-balance',
        className
      )}
      {...props}
    >
      {children}
    </h3>
  )
))

CardTitle.displayName = 'CardTitle'

const CardDescription = memo(forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, children, ...props }, ref) => (
    <p 
      ref={ref} 
      className={cn(
        'text-sm leading-relaxed',
        'text-gray-600 dark:text-gray-400',
        'text-balance',
        className
      )} 
      {...props}
    >
      {children}
    </p>
  )
))

CardDescription.displayName = 'CardDescription'

const CardContent = memo(forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { compact?: boolean }>(
  ({ className, children, compact, ...props }, ref) => (
    <div 
      ref={ref} 
      className={cn(
        compact ? 'space-y-2' : 'space-y-4',
        className
      )} 
      {...props}
    >
      {children}
    </div>
  )
))

CardContent.displayName = 'CardContent'

const CardFooter = memo(forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div 
      ref={ref} 
      className={cn(
        'flex items-center justify-between pt-4 mt-4',
        'border-t border-gray-200 dark:border-gray-700',
        className
      )} 
      {...props}
    >
      {children}
    </div>
  )
))

CardFooter.displayName = 'CardFooter'

// Specialized metric card for dashboard
export const MetricCard = memo(forwardRef<HTMLDivElement, CardProps & {
  title: string
  value: string | number
  description?: string
  trend?: { value: number; direction: 'up' | 'down' | 'stable' }
  icon?: React.ReactNode
}>(({ title, value, description, trend, icon, className, ...props }, ref) => (
  <Card
    ref={ref}
    variant="metric"
    className={cn('relative overflow-hidden', className)}
    {...props}
  >
    <CardContent className="space-y-0">
      {/* Header with icon */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            {title}
          </h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
            {value}
          </p>
        </div>
        {icon && (
          <div className="flex-shrink-0 ml-4">
            <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              {icon}
            </div>
          </div>
        )}
      </div>
      
      {/* Description and trend */}
      <div className="flex items-center justify-between">
        {description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {description}
          </p>
        )}
        
        {trend && (
          <div className={cn(
            'flex items-center gap-1 text-xs font-semibold',
            {
              'text-green-600 dark:text-green-400': trend.direction === 'up',
              'text-red-600 dark:text-red-400': trend.direction === 'down',
              'text-gray-500 dark:text-gray-400': trend.direction === 'stable',
            }
          )}>
            <span className="text-base">
                {trend.direction === 'up' && '↗'}
                {trend.direction === 'down' && '↘'}
              {trend.direction === 'stable' && '→'}
            </span>
            <span>
              {trend.value > 0 ? '+' : ''}{trend.value}%
            </span>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
)))

MetricCard.displayName = 'MetricCard'

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent } 